/** Demo Mode only - Finance > Contracts tab (Contract Performance). Summary table with
 * sort/filter/search following the standard table container + quick-filter-chip patterns.
 * Selecting a row navigates to the contract drill-down (ContractDetail.tsx). */

import { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import CustomPagination from '../../../components/forms/Pagination';
import { applyRoundAndFormat } from '../../../utils';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { classifyFinancialStatus, computeTotals } from '../../../mocks/data/financeData';
import { FinancialStatus } from '../../../mocks/data/financeData';
import { useFinanceFilters } from '../FinanceFiltersContext';
import { financialStatusColor } from '../financeExceptions';

const STATUS_FILTERS: FinancialStatus[] = ['Ahead of plan', 'On plan', 'At risk', 'Materially behind plan'];
const PAGE_SIZE = 10;

const ContractsTab = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { scopedContracts } = useFinanceFilters();
  const [statusFilter, setStatusFilter] = useState<FinancialStatus | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => scopedContracts
    .map((contract) => {
      const budget = computeTotals(contract.scenarios.budget);
      const forecast = computeTotals(contract.scenarios.currentForecast);
      const status = classifyFinancialStatus(contract);
      return {
        contract, budget, forecast, status,
      };
    })
    .filter((row) => !statusFilter || row.status === statusFilter)
    .filter((row) => !search || `${row.contract.client} ${row.contract.contractName} ${row.contract.site}`.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b.forecast.ebit - a.forecast.ebit), [scopedContracts, statusFilter, search]);

  const pagedRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map((status) => (
          <Chip key={status}
            label={t(`financeStatus${status.replace(/\s/g, '')}`, status)}
            color={statusFilter === status ? financialStatusColor[status] : 'default'}
            onClick={() => { setStatusFilter((prev) => (prev === status ? '' : status)); setPage(0); }} />
        ))}
        <TextField
          size="small"
          label={t('financeContractsSearch', 'Search client/contract/site')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ ml: 'auto', minWidth: 260, mb: 0 }}
        />
      </Box>

      <Section>
        <TableHeader>
          {t('financeContractsTableTitle', 'Contract performance')}
          <Pill text={`${rows.length} ${t('financeContractsLabel', 'contracts')}`} />
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('commercialPerformanceCustomer', 'Client')}</TableCell>
                <TableCell>{t('commercialPerformanceContract', 'Contract')}</TableCell>
                <TableCell>{t('commercialPerformanceSite', 'Site')}</TableCell>
                <TableCell>{t('financeSector', 'Sector')}</TableCell>
                <TableCell align="right">{t('commercialPerformanceSales', 'Revenue')}</TableCell>
                <TableCell align="right">{t('commercialPerformanceGrossMargin', 'GP%')}</TableCell>
                <TableCell align="right">{t('financeContractsFoodCostPercentage', 'Food cost%')}</TableCell>
                <TableCell align="right">{t('financeContractsLabourCostPercentage', 'Labour cost%')}</TableCell>
                <TableCell align="right">{t('financePnlEbit', 'EBIT')}</TableCell>
                <TableCell align="right">{t('financePnlEbitMargin', 'EBIT margin')}</TableCell>
                <TableCell align="right">{t('financeContractsBudgetVariance', 'Budget variance')}</TableCell>
                <TableCell>{t('financeContractsFinancialStatus', 'Financial status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(({ contract, budget, forecast, status }) => (
                <TableRow key={contract.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/finance-performance/contracts/${contract.id}`)}>
                  <TableCell>{contract.client}</TableCell>
                  <TableCell>{contract.contractName}</TableCell>
                  <TableCell>{contract.site}</TableCell>
                  <TableCell>{contract.sector}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(forecast.totalRevenue, currencyCode)}</TableCell>
                  <TableCell align="right">{`${forecast.grossProfitPercentage.toFixed(1)}%`}</TableCell>
                  <TableCell align="right">{`${((forecast.totalCostOfSales / forecast.totalRevenue) * 100).toFixed(1)}%`}</TableCell>
                  <TableCell align="right">{`${forecast.labourPercentage.toFixed(1)}%`}</TableCell>
                  <TableCell align="right"
                    sx={{ color: forecast.ebit < 0 ? theme.palette.error.main : 'inherit' }}>
                    {forecast.ebit < 0 ? `(${applyRoundAndFormat(Math.abs(forecast.ebit), currencyCode)})` : applyRoundAndFormat(forecast.ebit, currencyCode)}
                  </TableCell>
                  <TableCell align="right">{`${forecast.ebitMarginPercentage.toFixed(1)}%`}</TableCell>
                  <TableCell align="right"
                    sx={{ color: forecast.ebit - budget.ebit < 0 ? theme.palette.error.main : theme.palette.success.main }}>
                    {`${forecast.ebit - budget.ebit < 0 ? '(' : '+'}${applyRoundAndFormat(Math.abs(forecast.ebit - budget.ebit), currencyCode)}${forecast.ebit - budget.ebit < 0 ? ')' : ''}`}
                  </TableCell>
                  <TableCell>
                    <Chip size="small"
                      label={t(`financeStatus${status.replace(/\s/g, '')}`, status)}
                      color={financialStatusColor[status]}
                      variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination page={page}
          setPage={setPage}
          maxPages={Math.max(1, Math.ceil(rows.length / PAGE_SIZE))}
          useNoDataMessage={false}
          breakpointForChangeDisplay={120000} />
      </Section>
    </Box>
  );
};

export default ContractsTab;
